import {
  CaseState,
  CasePriority,
  getCaseStateLabel,
  OPEN_CASE_STATES,
} from "../types/case";
import type { Case } from "../types/case";
import type {
  CaseStatusMetrics,
  PriorityBreakdown,
  SLAComplianceData,
  AvgResolutionTimeData,
  OverdueAgingBand,
  UnassignedCasesData,
  AssigneeWorkloadEntry,
  ComplianceFunnelStage,
  DueDateForecastBucket,
  ThroughputDataPoint,
  RuleCaseVolumeEntry,
  ChapterCaseVolumeEntry,
  ChapterCompletionEntry,
  RuleSetCaseGroup,
  RejectionRateData,
  CreationTypeEntry,
  RiskExposureData,
  UrgencyMatrixPoint,
  StaleCaseEntry,
  HeatmapCell,
} from "../types/dashboard";

// ─── Terminal states used across multiple functions ───────────────────────────

export const TERMINAL_STATES = new Set<number>([
  CaseState.Closed,
  CaseState.Resolved,
  CaseState.ComplianceApproved,
  CaseState.ComplianceClosed,
  CaseState.ComplianceConfirmed,
  CaseState.Waived,
]);

// ─── Helpers ─────────────────────────────────────────────────────────────────

function diffDays(a: Date, b: Date): number {
  return (a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24);
}

function isoWeek(date: Date): string {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function isoMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

// ─── 1. Status Metrics (extracted from CaseManagementDashboard) ─────────────

export function computeStatusMetrics(cases: Case[]): CaseStatusMetrics {
  const now = new Date();
  let open = 0;
  let openCases = 0;
  let inProgress = 0;
  let resolved = 0;
  let closed = 0;
  let overdue = 0;

  const openStatesSet = new Set<number>(OPEN_CASE_STATES);

  for (const c of cases) {
    const s = c.state;
    if (openStatesSet.has(s as number)) openCases++;
    if (s === CaseState.Open) open++;
    else if (
      s === CaseState.InProgress ||
      s === CaseState.AwaitingUpload ||
      s === CaseState.PendingReview ||
      s === CaseState.ComplianceReview
    )
      inProgress++;
    else if (s === CaseState.Resolved) resolved++;
    else if (
      s === CaseState.Closed ||
      s === CaseState.ComplianceApproved ||
      s === CaseState.ComplianceClosed ||
      s === CaseState.ComplianceConfirmed ||
      s === CaseState.Waived
    )
      closed++;

    // Overdue: open/in-progress with dueDate in the past (excluding COMPLIANCE_EXEMPTED cases)
    if (
      c.dueDate &&
      !TERMINAL_STATES.has(s as number) &&
      c.caseStatus !== "COMPLIANCE_EXEMPTED"
    ) {
      if (new Date(c.dueDate) < now) overdue++;
    }
  }

  return {
    total: cases.length,
    open,
    openCases,
    inProgress,
    resolved,
    closed,
    overdue,
  };
}

// ─── 2. Priority Breakdown (extracted from CaseManagementDashboard) ──────────

export function computePriorityBreakdown(cases: Case[]): PriorityBreakdown {
  let critical = 0;
  let high = 0;
  let medium = 0;
  let low = 0;

  for (const c of cases) {
    const p = c.priority?.toUpperCase();
    if (p === CasePriority.CRITICAL) critical++;
    else if (p === CasePriority.HIGH) high++;
    else if (p === CasePriority.MEDIUM) medium++;
    else low++;
  }
  return { critical, high, medium, low };
}

// ─── 3. Resolved Today (extracted from CaseManagementDashboard) ──────────────

export function computeResolvedToday(cases: Case[]): number {
  const todayStr = new Date().toISOString().slice(0, 10);
  return cases.filter(
    (c) =>
      (c.state === CaseState.Resolved ||
        c.state === CaseState.Closed ||
        c.state === CaseState.ComplianceClosed ||
        c.state === CaseState.ComplianceConfirmed) &&
      c.updatedAt?.slice(0, 10) === todayStr,
  ).length;
}

// ─── 4. SLA Compliance ──────────────────────────────────────────────────────

export function computeSLACompliance(cases: Case[]): SLAComplianceData {
  let totalResolved = 0;
  let withinSLA = 0;

  for (const c of cases) {
    if (!TERMINAL_STATES.has(c.state as number)) continue;
    if (!c.dueDate) continue;
    totalResolved++;
    if (new Date(c.updatedAt) <= new Date(c.dueDate)) {
      withinSLA++;
    }
  }

  const breachedSLA = totalResolved - withinSLA;
  const complianceRate =
    totalResolved > 0 ? Math.round((withinSLA / totalResolved) * 100) : 0;

  return { totalResolved, withinSLA, breachedSLA, complianceRate };
}

// ─── 5. Average Resolution Time ─────────────────────────────────────────────

export function computeAvgResolutionTime(cases: Case[]): AvgResolutionTimeData {
  let totalDays = 0;
  let sampleSize = 0;

  for (const c of cases) {
    if (!TERMINAL_STATES.has(c.state as number)) continue;
    const created = new Date(c.createdAt);
    const updated = new Date(c.updatedAt);
    const days = diffDays(updated, created);
    if (days >= 0) {
      totalDays += days;
      sampleSize++;
    }
  }

  const avgDays =
    sampleSize > 0 ? Math.round((totalDays / sampleSize) * 10) / 10 : 0;
  return { avgDays, sampleSize };
}

// ─── 6. Overdue Aging Bands ─────────────────────────────────────────────────

export function computeOverdueAgingBands(cases: Case[]): OverdueAgingBand[] {
  const now = new Date();
  const bands: OverdueAgingBand[] = [
    { label: "1–7 days", min: 1, max: 7, count: 0 },
    { label: "8–14 days", min: 8, max: 14, count: 0 },
    { label: "15–30 days", min: 15, max: 30, count: 0 },
    { label: "30+ days", min: 31, max: null, count: 0 },
  ];

  for (const c of cases) {
    if (
      !c.dueDate ||
      TERMINAL_STATES.has(c.state as number) ||
      c.caseStatus === "COMPLIANCE_EXEMPTED"
    )
      continue;
    const due = new Date(c.dueDate);
    if (due >= now) continue;
    const daysOverdue = Math.ceil(diffDays(now, due));
    for (const band of bands) {
      if (
        daysOverdue >= band.min &&
        (band.max === null || daysOverdue <= band.max)
      ) {
        band.count++;
        break;
      }
    }
  }

  return bands;
}

// ─── 7. Unassigned Cases ────────────────────────────────────────────────────

export function computeUnassignedCases(cases: Case[]): UnassignedCasesData {
  const activeCases = cases.filter(
    (c) => !TERMINAL_STATES.has(c.state as number),
  );
  const unassigned = activeCases.filter((c) => !c.assignedTo);
  return {
    count: unassigned.length,
    totalActive: activeCases.length,
    cases: unassigned,
  };
}

// ─── 8. Assignee Workload ───────────────────────────────────────────────────

export function computeAssigneeWorkload(
  cases: Case[],
  getUserDisplayName?: (userId: string) => string,
): AssigneeWorkloadEntry[] {
  const map = new Map<string, { displayName: string; count: number }>();

  for (const c of cases) {
    if (TERMINAL_STATES.has(c.state as number)) continue;
    if (!c.assignedTo) continue;

    const assigneeId = String(c.assignedTo.id);
    const existing = map.get(assigneeId);
    if (existing) {
      existing.count++;
    } else {
      let displayName: string;
      if (getUserDisplayName) {
        const resolved = getUserDisplayName(assigneeId);
        if (/^User \d+$/.test(resolved) && c.assignedTo) {
          displayName =
            c.assignedTo.name ||
            `${c.assignedTo.firstName ?? ""} ${c.assignedTo.lastName ?? ""}`.trim() ||
            resolved;
        } else {
          displayName = resolved;
        }
      } else {
        displayName =
          c.assignedTo.name ||
          `${c.assignedTo.firstName ?? ""} ${c.assignedTo.lastName ?? ""}`.trim() ||
          "Unknown";
      }
      map.set(assigneeId, { displayName, count: 1 });
    }
  }

  return Array.from(map.entries())
    .map(([assigneeId, { displayName, count }]) => ({
      assigneeId,
      displayName,
      openCaseCount: count,
    }))
    .sort((a, b) => b.openCaseCount - a.openCaseCount);
}

// ─── 9. Compliance Funnel ───────────────────────────────────────────────────

export function computeComplianceFunnel(
  cases: Case[],
): ComplianceFunnelStage[] {
  const stageOrder: number[] = [
    CaseState.Open,
    CaseState.InProgress,
    CaseState.ComplianceReview,
    CaseState.ComplianceEscalated,
    CaseState.ComplianceApproved,
    CaseState.ComplianceConfirmed,
    CaseState.ComplianceClosed,
  ];

  const counts = new Map<number, number>();
  for (const c of cases) {
    counts.set(c.state as number, (counts.get(c.state as number) ?? 0) + 1);
  }

  return stageOrder.map((state) => ({
    stage: getCaseStateLabel(state),
    count: counts.get(state) ?? 0,
  }));
}

// ─── 10. Due Date Forecast ──────────────────────────────────────────────────

export function computeDueDateForecast(cases: Case[]): DueDateForecastBucket[] {
  const now = new Date();
  const buckets: DueDateForecastBucket[] = [
    { label: "Next 7 days", daysAhead: 7, count: 0 },
    { label: "8–14 days", daysAhead: 14, count: 0 },
    { label: "15–30 days", daysAhead: 30, count: 0 },
  ];

  for (const c of cases) {
    if (!c.dueDate || TERMINAL_STATES.has(c.state as number)) continue;
    const due = new Date(c.dueDate);
    const daysUntilDue = diffDays(due, now);
    if (daysUntilDue < 0) continue; // already overdue
    if (daysUntilDue <= 7) buckets[0].count++;
    else if (daysUntilDue <= 14) buckets[1].count++;
    else if (daysUntilDue <= 30) buckets[2].count++;
  }

  return buckets;
}

// ─── 11. Throughput Trend ───────────────────────────────────────────────────

export function computeThroughputTrend(
  cases: Case[],
  granularity: "weekly" | "monthly" = "weekly",
): ThroughputDataPoint[] {
  const openedMap = new Map<string, number>();
  const closedMap = new Map<string, number>();
  const periodFn = granularity === "weekly" ? isoWeek : isoMonth;

  for (const c of cases) {
    const openedPeriod = periodFn(new Date(c.createdAt));
    openedMap.set(openedPeriod, (openedMap.get(openedPeriod) ?? 0) + 1);

    if (TERMINAL_STATES.has(c.state as number)) {
      const closedPeriod = periodFn(new Date(c.updatedAt));
      closedMap.set(closedPeriod, (closedMap.get(closedPeriod) ?? 0) + 1);
    }
  }

  const allPeriods = new Set([...openedMap.keys(), ...closedMap.keys()]);
  return Array.from(allPeriods)
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .map((period) => ({
      period,
      opened: openedMap.get(period) ?? 0,
      closed: closedMap.get(period) ?? 0,
    }));
}

// ─── 12. Top Rules by Case Volume ───────────────────────────────────────────

export function computeTopRulesByVolume(
  cases: Case[],
  rules?: Array<{ id: number; name: string }>,
  limit = 10,
): RuleCaseVolumeEntry[] {
  const countMap = new Map<number, number>();

  for (const c of cases) {
    if (c.ruleId !== null && c.ruleId !== undefined) {
      countMap.set(c.ruleId, (countMap.get(c.ruleId) ?? 0) + 1);
    }
  }

  const ruleNameMap = new Map<number, string>();
  if (rules) {
    for (const r of rules) {
      ruleNameMap.set(r.id, r.name);
    }
  }

  return Array.from(countMap.entries())
    .map(([ruleId, caseCount]) => ({
      ruleId,
      ruleName: ruleNameMap.get(ruleId) ?? `Rule #${ruleId}`,
      caseCount,
    }))
    .sort((a, b) => b.caseCount - a.caseCount)
    .slice(0, limit);
}

// ─── 13. Rule Set Groups ────────────────────────────────────────────────────

export function computeChapterVolume(
  cases: Case[],
  rules?: Array<{ id: number; manualReference?: string | null }>,
  limit = 10,
): ChapterCaseVolumeEntry[] {
  const ruleChapterMap = new Map<number, string>();
  if (rules) {
    for (const r of rules) {
      if (r.manualReference) {
        ruleChapterMap.set(r.id, r.manualReference);
      }
    }
  }

  const countMap = new Map<string, number>();

  for (const c of cases) {
    const chapter =
      (c.chapter as string) ||
      (c.ruleId != null ? ruleChapterMap.get(c.ruleId) : undefined) ||
      "Uncategorized";
    countMap.set(chapter, (countMap.get(chapter) ?? 0) + 1);
  }

  return Array.from(countMap.entries())
    .map(([chapter, caseCount]) => ({ chapter, caseCount }))
    .sort((a, b) => b.caseCount - a.caseCount)
    .slice(0, limit);
}

export function computeChapterCompletion(
  cases: Case[],
  rules?: Array<{ id: number; manualReference?: string | null }>,
  limit = 10,
): ChapterCompletionEntry[] {
  const ruleChapterMap = new Map<number, string>();
  if (rules) {
    for (const r of rules) {
      if (r.manualReference) {
        ruleChapterMap.set(r.id, r.manualReference);
      }
    }
  }

  const chapterStats = new Map<string, { total: number; resolved: number }>();

  for (const c of cases) {
    const chapter =
      (c.chapter as string) ||
      (c.ruleId != null ? ruleChapterMap.get(c.ruleId) : undefined) ||
      "Uncategorized";
    const entry = chapterStats.get(chapter) ?? { total: 0, resolved: 0 };
    entry.total++;
    if (TERMINAL_STATES.has(c.state as number)) {
      entry.resolved++;
    }
    chapterStats.set(chapter, entry);
  }

  return Array.from(chapterStats.entries())
    .map(([chapter, stats]) => ({
      chapter,
      total: stats.total,
      resolved: stats.resolved,
      completionRate:
        stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0,
    }))
    .sort((a, b) => a.completionRate - b.completionRate)
    .slice(0, limit);
}

export function computeRuleSetGroups(
  cases: Case[],
  rules?: Array<{
    id: number;
    name: string;
    ruleSet?: { name?: string; displayName?: string };
  }>,
): RuleSetCaseGroup[] {
  const ruleSetMap = new Map<number, string>();
  if (rules) {
    for (const r of rules) {
      const setName = r.ruleSet?.displayName ?? r.ruleSet?.name ?? "Ungrouped";
      ruleSetMap.set(r.id, setName);
    }
  }

  const groupMap = new Map<
    string,
    { total: number; open: number; resolved: number; closed: number }
  >();
  for (const c of cases) {
    const setName =
      c.ruleId !== null && c.ruleId !== undefined
        ? (ruleSetMap.get(c.ruleId) ?? "Ungrouped")
        : "No Rule";
    const entry = groupMap.get(setName) ?? {
      total: 0,
      open: 0,
      resolved: 0,
      closed: 0,
    };
    entry.total++;
    if (TERMINAL_STATES.has(c.state as number)) {
      entry.closed++;
    } else {
      entry.open++;
    }
    groupMap.set(setName, entry);
  }

  return Array.from(groupMap.entries())
    .map(([ruleSetName, counts]) => ({
      ruleSetName,
      caseCount: counts.total,
      open: counts.open,
      resolved: counts.resolved,
      closed: counts.closed,
    }))
    .sort((a, b) => b.caseCount - a.caseCount);
}

// ─── 14. Rejection Rate ─────────────────────────────────────────────────────

export function computeRejectionRate(cases: Case[]): RejectionRateData {
  const totalCases = cases.length;
  const rejectedCount = cases.filter(
    (c) =>
      c.state === CaseState.Rejected ||
      c.state === CaseState.ComplianceRejected,
  ).length;

  const rejectionRate =
    totalCases > 0 ? Math.round((rejectedCount / totalCases) * 100) : 0;
  return { totalCases, rejectedCount, rejectionRate };
}

// ─── 15. Creation Type Breakdown ────────────────────────────────────────────

export function computeCreationTypeBreakdown(
  cases: Case[],
): CreationTypeEntry[] {
  // COMPLIANCE_CONFIRMED is a case state, not a creation type — exclude it
  const EXCLUDED_TYPES = new Set(["COMPLIANCE_CONFIRMED"]);
  const countMap = new Map<string, number>();

  for (const c of cases) {
    const type = c.creationType ?? "UNKNOWN";
    if (EXCLUDED_TYPES.has(type)) continue;
    countMap.set(type, (countMap.get(type) ?? 0) + 1);
  }

  return Array.from(countMap.entries())
    .map(([creationType, count]) => ({ creationType, count }))
    .sort((a, b) => b.count - a.count);
}

// ─── 16. Risk Exposure ──────────────────────────────────────────────────────

const PRIORITY_WEIGHT: Record<string, number> = {
  [CasePriority.CRITICAL]: 4,
  [CasePriority.HIGH]: 3,
  [CasePriority.MEDIUM]: 2,
  [CasePriority.LOW]: 1,
};

const SEVERITY_WEIGHT: Record<string, number> = {
  CRITICAL: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

export function computeRiskExposure(cases: Case[]): RiskExposureData {
  const now = new Date();
  let totalScore = 0;
  let highRiskCount = 0;

  const activeCases = cases.filter(
    (c) => !TERMINAL_STATES.has(c.state as number),
  );

  for (const c of activeCases) {
    const priorityScore = PRIORITY_WEIGHT[c.priority?.toUpperCase() ?? ""] ?? 1;
    const severityScore = SEVERITY_WEIGHT[c.severity?.toUpperCase() ?? ""] ?? 1;

    let overdueScore = 0;
    if (c.dueDate && new Date(c.dueDate) < now) {
      const daysOverdue = diffDays(now, new Date(c.dueDate));
      overdueScore = Math.min(daysOverdue / 7, 4); // cap at 4
    }

    // Weighted: priority 40%, severity 30%, overdue 30%
    const score =
      priorityScore * 0.4 + severityScore * 0.3 + overdueScore * 0.3;
    totalScore += score;
    if (score >= 2.5) highRiskCount++;
  }

  const avgScore =
    activeCases.length > 0
      ? Math.round((totalScore / activeCases.length) * 10) / 10
      : 0;

  return {
    totalScore: Math.round(totalScore * 10) / 10,
    avgScore,
    highRiskCount,
  };
}

// ─── 17. Urgency Matrix ────────────────────────────────────────────────────

export function computeUrgencyMatrix(cases: Case[]): UrgencyMatrixPoint[] {
  const now = new Date();
  const points: UrgencyMatrixPoint[] = [];

  for (const c of cases) {
    if (TERMINAL_STATES.has(c.state as number)) continue;
    if (!c.dueDate) continue;

    const daysToDue = Math.round(diffDays(new Date(c.dueDate), now));
    points.push({
      caseId: c.id,
      title: c.title,
      priority: c.priority?.toUpperCase() ?? "UNKNOWN",
      daysToDue,
    });
  }

  return points;
}

// ─── 18. Stale Cases ────────────────────────────────────────────────────────

export function computeStaleCases(
  cases: Case[],
  thresholdDays = 14,
): StaleCaseEntry[] {
  const now = new Date();

  return cases
    .filter((c) => {
      if (TERMINAL_STATES.has(c.state as number)) return false;
      const daysSinceUpdate = diffDays(now, new Date(c.updatedAt));
      return daysSinceUpdate >= thresholdDays;
    })
    .map((c) => ({
      caseId: c.id,
      title: c.title,
      state: c.state,
      daysSinceUpdate: Math.round(diffDays(now, new Date(c.updatedAt))),
    }))
    .sort((a, b) => b.daysSinceUpdate - a.daysSinceUpdate);
}

// ─── 19. Priority × Status Heatmap ─────────────────────────────────────────

export function computePriorityStatusHeatmap(cases: Case[]): HeatmapCell[] {
  const priorities = [
    CasePriority.CRITICAL,
    CasePriority.HIGH,
    CasePriority.MEDIUM,
    CasePriority.LOW,
  ];
  const stateValues = [
    CaseState.Open,
    CaseState.InProgress,
    CaseState.ComplianceReview,
    CaseState.ComplianceEscalated,
    CaseState.ComplianceApproved,
    CaseState.ComplianceConfirmed,
    CaseState.ComplianceClosed,
    CaseState.Resolved,
    CaseState.Closed,
  ];

  const countMap = new Map<string, number>();
  for (const c of cases) {
    const p = c.priority?.toUpperCase() ?? "LOW";
    const key = `${p}|${c.state}`;
    countMap.set(key, (countMap.get(key) ?? 0) + 1);
  }

  const cells: HeatmapCell[] = [];
  for (const priority of priorities) {
    for (const state of stateValues) {
      const key = `${priority}|${state}`;
      cells.push({
        priority,
        state: getCaseStateLabel(state),
        count: countMap.get(key) ?? 0,
      });
    }
  }

  return cells;
}
